import { Box, Typography } from "@mui/material";

import { ds } from "../../theme/ds";
import { palette } from "../../theme/tokens";
import type { WidgetProps } from "../types";

import { CalloutBox } from "./Callout";
import { Card, CardBody, CardLabel } from "./card";

// The first section of the Opportunity page (Bill, 2026-09-08, restyled the
// same evening on his review). It opens with the projection: an eyebrow, the
// headline claim in Bison blue at figure size, then the challenges we heard
// and saw as one paragraph, so the cards that follow answer something. Then
// the cards, one per lever in opportunity order: the lever as a `.kpi-label`,
// the sourced figure in Bison blue at 800 weight with air above and below,
// its label, the lever's finding as a danger callout in red (it names a problem; Bill, 2026-09-08),
// then "Your situation today" (red label) and "What this unlocks" (green
// label; "With Bison" until 2026-10-25, Bill) as two separated blocks. No source line: Bill does not want it on these cards
// (the figures' sources stay in the data for the provisioning skill).
export function LeverCards({ widget }: WidgetProps<"lever_cards">) {
    const { title, intro, headline, challenges } = widget.props;
    const { levers } = widget.data;

    return (
        <Box>
            {(headline || challenges) && (
                <Card sx={{ mb: "24px" }}>
                    <CardLabel>Your opportunity with Bison</CardLabel>
                    {headline && (
                        <Typography
                            component="div"
                            sx={{
                                fontSize: { xs: ds.text.xl, md: ds.text["2xl"] },
                                fontWeight: 800,
                                letterSpacing: "-0.02em",
                                lineHeight: 1.15,
                                color: palette.blue,
                                pt: "12px",
                                pb: "12px",
                            }}
                        >
                            {headline}
                        </Typography>
                    )}
                    {challenges && <CardBody sx={{ fontSize: ds.text.base, maxWidth: 900 }}>{challenges}</CardBody>}
                </Card>
            )}

            {title && (
                <Typography variant="h4" sx={{ mb: intro ? 0.5 : 2 }}>
                    {title}
                </Typography>
            )}
            {intro && (
                <Typography
                    component="div"
                    sx={{ fontSize: ds.text.sm, color: ds.gray600, mb: 2, maxWidth: 760 }}
                >
                    {intro}
                </Typography>
            )}
            <Box
                sx={{
                    display: "grid",
                    // Four levers sit in one row, never three plus an orphan (Bill, 2026-10-28).
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        lg: `repeat(${levers.length === 4 ? 4 : 3}, 1fr)`,
                    },
                    gap: "16px",
                }}
            >
                {levers.map((l) => (
                    <Card key={l.wedge} sx={{ display: "flex", flexDirection: "column" }}>
                        <CardLabel>{l.wedge}</CardLabel>
                        {l.stat && (
                            <Box sx={{ pt: "16px", pb: "12px" }}>
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: ds.text["2xl"],
                                        fontWeight: 800,
                                        letterSpacing: "-0.02em",
                                        lineHeight: 1.1,
                                        color: palette.blue,
                                        mb: "6px",
                                    }}
                                >
                                    {l.stat.value}
                                </Typography>
                                <CardBody sx={{ lineHeight: 1.5 }}>{l.stat.label}</CardBody>
                            </Box>
                        )}
                        <Box sx={{ mt: "4px", mb: "12px" }}>
                            <CalloutBox variant="danger" body={l.title} />
                        </Box>
                        <Block label="Your situation today" color={ds.danger.text} text={l.today} />
                        <Block label="What this unlocks"color={palette.green} text={l.withBison} divider />
                    </Card>
                ))}
            </Box>
        </Box>
    );
}

function Block({ label, color, text, divider }: { label: string; color: string; text: string; divider?: boolean }) {
    return (
        <Box
            sx={{
                pt: "12px",
                ...(divider && { mt: "12px", borderTop: `1px solid ${ds.gray200}` }),
            }}
        >
            <CardLabel sx={{ color, fontWeight: 700 }}>{label}</CardLabel>
            <CardBody sx={{ mt: "4px" }}>{text}</CardBody>
        </Box>
    );
}
